import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { audienceService, newsletterService } from '../../services';
import { Panel, PanelHeader, Pill, MUTED, SUBTLE, HAIRLINE, useSoft } from '../surface';
import TintSquare from '../hand/TintSquare';
import { inputClass, inputStyle } from './ui';

export const AUDIENCE_KINDS = [
  { key: 'people', label: 'SPECIFIC PEOPLE' },
  { key: 'followers', label: 'FOLLOWERS' },
  { key: 'everyone', label: 'EVERYONE' },
  { key: 'contacts', label: 'CONTACT LIST' },
];

export const emptyAudience = () => ({ userIds: [], contactIds: [], groupIds: [] });

/**
 * WHO GETS IT — the three audiences a page can send to. SPECIFIC PEOPLE is a search-select
 * over followers / members and contacts; FOLLOWERS is everyone following (MEMBERS on a club);
 * EVERYONE is followers plus the whole contact list, deduped; CONTACT LIST is the list or the
 * ticked groups. The REACH line is the server's count
 * for this channel, so no-address and opted-out people are already out of it.
 *
 * value: { kind, audience: { userIds, contactIds, groupIds }, picked: [AudiencePersonDto] }.
 * `picked` carries the chosen people's names so a reopened draft can show them.
 */
/** The admin newsletter's audiences (Email Studio §4 "03 Who gets it"). */
export const ADMIN_AUDIENCE_KINDS = [
  { key: 'everyone', label: 'EVERYONE' },
  { key: 'city', label: 'BY CITY' },
  { key: 'hosts', label: 'HOSTS ONLY' },
  { key: 'test', label: 'TEST LIST' },
];

export default function AudiencePicker(props) {
  const soft = useSoft();
  if (props.mode === 'admin') return <AdminAudience {...props} />;
  if (soft) return <SoftPageAudience {...props} />;
  return <HardPageAudience {...props} />;
}

function HardPageAudience({ ownerType, ownerId, channel, followerWord = 'FOLLOWERS', value, onChange, readOnly = false, onReach }) {
  const kind = value?.kind || 'followers';
  const audience = value?.audience || emptyAudience();
  const picked = value?.picked || [];
  const [groups, setGroups] = useState(null);
  const [reach, setReach] = useState(null);

  useEffect(() => {
    let live = true;
    audienceService.listGroups(ownerType, ownerId).then((r) => { if (live && r.success) setGroups(r.data || []); });
    return () => { live = false; };
  }, [ownerType, ownerId]);

  // Debounced count: every change to kind / picks / groups asks the server what it reaches.
  const spec = useMemo(() => JSON.stringify({ channel, audienceKind: kind, audience }), [channel, kind, audience]);
  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      const r = await audienceService.countAudience(ownerType, ownerId, JSON.parse(spec));
      if (!live) return;
      const next = r.success ? r.data : null;
      setReach(next);
      onReach?.(next);
    }, 250);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerType, ownerId, spec]);

  const set = (patch) => onChange?.({ kind, audience, picked, ...patch });
  const setKind = (k) => set({ kind: k });
  const toggleGroup = (gid) => {
    const has = audience.groupIds.includes(gid);
    set({ audience: { ...audience, groupIds: has ? audience.groupIds.filter((g) => g !== gid) : [...audience.groupIds, gid] } });
  };
  const addPerson = (p) => {
    if (picked.some((x) => x.kind === p.kind && x.id === p.id)) return;
    const next = { ...audience };
    if (p.kind === 'user') next.userIds = [...audience.userIds, p.id]; else next.contactIds = [...audience.contactIds, p.id];
    set({ audience: next, picked: [...picked, p] });
  };
  const removePerson = (p) => {
    const next = { ...audience };
    if (p.kind === 'user') next.userIds = audience.userIds.filter((id) => id !== p.id); else next.contactIds = audience.contactIds.filter((id) => id !== p.id);
    set({ audience: next, picked: picked.filter((x) => !(x.kind === p.kind && x.id === p.id)) });
  };

  const labelFor = (k) => (k === 'followers' ? followerWord : AUDIENCE_KINDS.find((a) => a.key === k)?.label);
  const reachLine = reach ? `REACH ${reach.count.toLocaleString()}` : '…';

  return (
    <Panel data-testid="audience-picker">
      <PanelHeader label="WHO GETS IT" right={reachLine} />
      <div className="flex flex-col" style={{ padding: 16, gap: 14 }}>
        <div className="flex flex-wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Audience">
          {AUDIENCE_KINDS.map((a) => {
            const on = a.key === kind;
            return (
              <button key={a.key} type="button" role="radio" aria-checked={on} disabled={readOnly} onClick={() => setKind(a.key)} data-testid={`audience-${a.key}`}
                className={`border-2 border-black font-mono font-bold uppercase transition-colors duration-200 ${on ? 'bg-black text-white' : 'bg-white text-black hover:bg-black hover:text-white'} disabled:opacity-60`}
                style={{ padding: '8px 12px', fontSize: 10, letterSpacing: '0.1em' }}>
                {labelFor(a.key)}
              </button>
            );
          })}
        </div>

        {kind === 'followers' && (
          <span className="font-mono" style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
            Everyone who {followerWord === 'MEMBERS' ? 'is a member of the club' : 'follows the page'}, plus anyone on your list who has since made a Portal account. {channel === 'sms' ? 'Only those with a phone number on file.' : 'Anyone who turned off marketing email is left out.'}
          </span>
        )}

        {kind === 'contacts' && (
          <div className="flex flex-col" style={{ gap: 8 }}>
            <span className="font-mono" style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
              {audience.groupIds.length === 0 ? 'The whole contact list — everyone who joined by your link or was imported.' : 'Only the ticked groups.'}
            </span>
            {groups === null ? null : groups.length === 0 ? (
              <span className="font-mono" style={{ fontSize: 11, color: SUBTLE }}>No groups yet — make some on the CONTACTS tab to send to a slice of the list.</span>
            ) : (
              <div className="flex flex-wrap" style={{ gap: 6 }} data-testid="group-picks">
                {groups.map((g) => {
                  const on = audience.groupIds.includes(g.id);
                  return (
                    <button key={g.id} type="button" aria-pressed={on} disabled={readOnly} onClick={() => toggleGroup(g.id)}
                      className={`border-2 font-mono font-bold uppercase transition-colors duration-200 ${on ? 'border-black bg-black text-white' : 'bg-white text-black hover:border-black'}`}
                      style={{ padding: '6px 10px', fontSize: 10, letterSpacing: '0.1em', borderColor: on ? '#000' : HAIRLINE }}>
                      {g.name} <span style={{ opacity: 0.7 }}>· {g.count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {kind === 'people' && (
          <PeopleSearch ownerType={ownerType} ownerId={ownerId} channel={channel} picked={picked} onAdd={addPerson} onRemove={removePerson} readOnly={readOnly} />
        )}
      </div>
    </Panel>
  );
}

function PeopleSearch({ ownerType, ownerId, channel, picked, onAdd, onRemove, readOnly }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const search = useCallback(async (needle) => {
    const r = await audienceService.searchPeople(ownerType, ownerId, needle);
    setResults(r.success ? (r.data || []) : []);
  }, [ownerType, ownerId]);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), 200);
    return () => clearTimeout(timer.current);
  }, [q, open, search]);

  const sms = channel === 'sms';
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="people-search">
      {picked.length > 0 && (
        <div className="flex flex-wrap" style={{ gap: 6 }} data-testid="picked-people">
          {picked.map((p) => (
            <span key={`${p.kind}-${p.id}`} className="inline-flex items-center border-2 border-black bg-white font-mono" style={{ gap: 6, padding: '4px 4px 4px 8px', fontSize: 11 }}>
              <span className="font-bold">{p.name}</span>
              {p.sub && <span style={{ color: MUTED }}>{p.sub}</span>}
              {!readOnly && <button type="button" aria-label={`Remove ${p.name}`} onClick={() => onRemove(p)} className="font-bold hover:bg-black hover:text-white" style={{ padding: '0 6px' }}>✕</button>}
            </span>
          ))}
        </div>
      )}
      {!readOnly && (
        <div className="relative">
          <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Search followers and contacts by name, @handle, email or number" aria-label="Search people" className={inputClass} style={inputStyle} />
          {open && (
            <div className="absolute left-0 right-0 z-20 border-2 border-black bg-white" style={{ top: '100%', marginTop: -2, maxHeight: 280, overflowY: 'auto' }} data-testid="people-results">
              {results.length === 0 ? (
                <div className="px-3 py-3 font-mono" style={{ fontSize: 11, color: MUTED }}>{q ? 'Nobody matches that.' : 'Type to search.'}</div>
              ) : results.map((p) => {
                const chosen = picked.some((x) => x.kind === p.kind && x.id === p.id);
                const reachable = sms ? p.hasPhone : p.hasEmail;
                return (
                  <button key={`${p.kind}-${p.id}`} type="button" disabled={chosen} onMouseDown={(e) => e.preventDefault()} onClick={() => onAdd(p)}
                    className="flex w-full items-center gap-3 border-b-2 border-[#E5E7EB] px-3 py-2 text-left hover:bg-[#F5F5F5] disabled:opacity-40 last:border-b-0">
                    <TintSquare name={p.name} imageUrl={p.avatarUrl} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono font-bold" style={{ fontSize: 12 }}>{p.name}</span>
                      <span className="block truncate font-mono" style={{ fontSize: 10, color: MUTED }}>{p.sub || (p.kind === 'contact' ? 'CONTACT' : '')}</span>
                    </span>
                    <Pill tone={p.kind === 'user' ? 'black' : 'outline'} size="xs">{p.kind === 'user' ? 'FOLLOWER' : 'CONTACT'}</Pill>
                    {!reachable && <Pill tone="ghost" size="xs">{sms ? 'NO NUMBER' : 'NO EMAIL'}</Pill>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Email Studio (soft) ─────────────────────────────────────────────────────

const plural = (n, one, many) => `${Number(n || 0).toLocaleString()} ${n === 1 ? one : many}`;

function SoftCard({ title, right, children, testId }) {
  return (
    <section className="flex flex-col bg-white font-mono" style={{ borderRadius: 28, padding: 24, gap: 18 }} data-testid={testId}>
      <div className="flex items-baseline justify-between" style={{ gap: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.18em' }}>{title}</span>
        {right}
      </div>
      {children}
    </section>
  );
}

function Tile({ on, label, note, onClick, disabled, testId }) {
  return (
    <button type="button" role="radio" aria-checked={on} disabled={disabled} onClick={onClick} data-testid={testId}
      className="flex flex-col items-start text-left transition-colors duration-200 focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000] disabled:cursor-default"
      style={{ gap: 6, padding: '16px 18px', border: 'none', borderRadius: 22, background: on ? '#0A0A0A' : '#F2F2F2', color: on ? '#fff' : '#000', fontFamily: 'inherit' }}>
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em' }}>{label}</span>
      {note && <span style={{ fontSize: 10, lineHeight: 1.5, opacity: 0.7 }}>{note}</span>}
    </button>
  );
}

function OptOutNote({ reach }) {
  const n = reach?.optedOut || 0;
  return (
    <div style={{ padding: '14px 18px', borderRadius: 18, background: '#F5F5F5', fontSize: 11, lineHeight: 1.6, color: '#4B5563' }} data-testid="opt-out-note">
      {plural(n, 'person has', 'people have')} opted out and will be skipped. Everyone gets a one-tap unsubscribe at the bottom.
    </div>
  );
}

function ReachLine({ reach, noun }) {
  const n = reach?.count;
  return <span style={{ fontSize: 10, letterSpacing: '.12em', color: '#6B7280' }} data-testid="reach-line">REACH <b style={{ color: '#000' }}>{n == null ? '…' : `${n.toLocaleString()} ${n === 1 ? noun.replace(/S$/, '') : noun}`}</b></span>;
}

/** A page: MEMBERS (FOLLOWERS) · EVERYONE · SPECIFIC PEOPLE · CONTACT LIST, each tile with its own count. */
function SoftPageAudience({ ownerType, ownerId, channel = 'email', followerWord = 'FOLLOWERS', value, onChange, readOnly = false, onReach }) {
  const kind = value?.kind || 'followers';
  const audience = value?.audience || emptyAudience();
  const picked = value?.picked || [];
  const [reach, setReach] = useState(null);
  const [counts, setCounts] = useState({});
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    let live = true;
    audienceService.listGroups(ownerType, ownerId).then((r) => { if (live && r.success) setGroups(r.data || []); });
    // The tile notes: what each whole audience reaches on this channel.
    Promise.all(['followers', 'everyone', 'contacts'].map((k) => audienceService.countAudience(ownerType, ownerId, { channel, audienceKind: k, audience: emptyAudience() })))
      .then((rs) => { if (live) setCounts({ followers: rs[0].data?.count, everyone: rs[1].data?.count, contacts: rs[2].data?.count }); });
    return () => { live = false; };
  }, [ownerType, ownerId, channel]);

  const spec = useMemo(() => JSON.stringify({ channel, audienceKind: kind, audience }), [channel, kind, audience]);
  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      const r = await audienceService.countAudience(ownerType, ownerId, JSON.parse(spec));
      if (!live) return;
      const next = r.success ? r.data : null;
      setReach(next);
      onReach?.(next);
    }, 250);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerType, ownerId, spec]);

  const set = (patch) => onChange?.({ kind, audience, picked, ...patch });
  const n = (k) => (counts[k] == null ? '' : counts[k]);
  const tiles = [
    ['followers', followerWord, counts.followers == null ? 'Everyone who joined' : `${n('followers')} ${followerWord === 'MEMBERS' ? 'people who joined' : 'people who follow'}`],
    ['everyone', 'EVERYONE', counts.everyone == null ? 'Members, followers and your list' : `${n('everyone')} ${followerWord.toLowerCase()} and contacts`],
    ['people', 'SPECIFIC PEOPLE', 'Pick by name'],
    ['contacts', 'CONTACT LIST', counts.contacts == null ? 'Your uploaded list' : `${n('contacts')} from your uploaded list`],
  ];
  const toggleGroup = (gid) => {
    const has = audience.groupIds.includes(gid);
    set({ audience: { ...audience, groupIds: has ? audience.groupIds.filter((g) => g !== gid) : [...audience.groupIds, gid] } });
  };
  const addPerson = (p) => {
    if (picked.some((x) => x.kind === p.kind && x.id === p.id)) return;
    const next = { ...audience };
    if (p.kind === 'user') next.userIds = [...audience.userIds, p.id]; else next.contactIds = [...audience.contactIds, p.id];
    set({ audience: next, picked: [...picked, p] });
  };
  const removePerson = (p) => {
    const next = { ...audience };
    if (p.kind === 'user') next.userIds = audience.userIds.filter((x) => x !== p.id); else next.contactIds = audience.contactIds.filter((x) => x !== p.id);
    set({ audience: next, picked: picked.filter((x) => !(x.kind === p.kind && x.id === p.id)) });
  };

  return (
    <SoftCard title="WHO GETS IT" right={<ReachLine reach={reach} noun="PEOPLE" />} testId="audience-picker">
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }} role="radiogroup" aria-label="Audience">
        {tiles.map(([k, label, note]) => <Tile key={k} on={kind === k} label={label} note={note} disabled={readOnly} onClick={() => set({ kind: k })} testId={`audience-${k}`} />)}
      </div>
      {kind === 'contacts' && groups && groups.length > 0 && (
        <div className="flex flex-wrap" style={{ gap: 8 }} data-testid="group-picks">
          {groups.map((g) => {
            const on = audience.groupIds.includes(g.id);
            return <button key={g.id} type="button" aria-pressed={on} disabled={readOnly} onClick={() => toggleGroup(g.id)} style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 999, background: on ? '#0A0A0A' : '#F2F2F2', color: on ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{g.name} · {g.count}</button>;
          })}
        </div>
      )}
      {kind === 'people' && <SoftPeople ownerType={ownerType} ownerId={ownerId} channel={channel} picked={picked} onAdd={addPerson} onRemove={removePerson} readOnly={readOnly} />}
      <OptOutNote reach={reach} />
    </SoftCard>
  );
}

const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function SoftPeople({ ownerType, ownerId, channel, picked, onAdd, onRemove, readOnly }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      const r = await audienceService.searchPeople(ownerType, ownerId, q);
      if (live) setResults(r.success ? (r.data || []) : []);
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [ownerType, ownerId, q]);
  const isOn = (p) => picked.some((x) => x.kind === p.kind && x.id === p.id);
  const shown = [...picked, ...results.filter((p) => !isOn(p))].slice(0, 40);
  const sms = channel === 'sms';
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="people-search">
      {!readOnly && (
        <div className="flex items-center" style={{ gap: 10, height: 46, padding: '0 16px', borderRadius: 999, background: '#F5F5F5' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search followers and contacts by name, @handle or email" aria-label="Search people"
            className="min-w-0 flex-1 border-none bg-transparent font-mono outline-none" style={{ fontSize: 12 }} />
        </div>
      )}
      <div className="flex flex-wrap" style={{ gap: 8 }} data-testid="picked-people">
        {shown.map((p) => {
          const on = isOn(p);
          const reachable = sms ? p.hasPhone : p.hasEmail;
          return (
            <button key={`${p.kind}-${p.id}`} type="button" aria-pressed={on} disabled={readOnly} onClick={() => (on ? onRemove(p) : onAdd(p))} title={reachable === false ? (sms ? 'No number on file' : 'No email on file') : undefined}
              className="flex items-center" style={{ height: 40, padding: '0 14px 0 4px', gap: 8, border: 'none', borderRadius: 999, background: on ? '#0A0A0A' : '#F2F2F2', color: on ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, opacity: reachable === false ? 0.55 : 1 }}>
              <span className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', background: on ? '#22ECFB' : '#fff', color: '#000', fontSize: 9, fontWeight: 800, overflow: 'hidden' }}>
                {p.avatarUrl ? <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(p.name)}
              </span>
              {p.name}
            </button>
          );
        })}
        {shown.length === 0 && <span style={{ fontSize: 11, color: MUTED }}>{q ? 'Nobody matches that.' : 'Nobody on the list yet.'}</span>}
      </div>
    </div>
  );
}

/** The admin newsletter: EVERYONE · BY CITY (multi-select) · HOSTS ONLY · TEST LIST. value: { kind, regionIds }. */
function AdminAudience({ value, onChange, readOnly = false, onReach }) {
  const kind = value?.kind || 'everyone';
  const regionIds = value?.regionIds || [];
  const [counts, setCounts] = useState({});
  const [cities, setCities] = useState(null);
  const [reach, setReach] = useState(null);

  useEffect(() => {
    let live = true;
    newsletterService.listCities().then((r) => { if (live) setCities(r.success ? (r.data || []) : []); });
    Promise.all(['everyone', 'hosts', 'test'].map((k) => newsletterService.countAudience({ audienceKind: k })))
      .then((rs) => { if (live) setCounts({ everyone: rs[0].data?.count, hosts: rs[1].data?.count, test: rs[2].data?.count }); });
    return () => { live = false; };
  }, []);

  const spec = useMemo(() => JSON.stringify({ audienceKind: kind, audience: { regionIds } }), [kind, regionIds]);
  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      const r = await newsletterService.countAudience(JSON.parse(spec));
      if (!live) return;
      const next = r.success ? r.data : null;
      setReach(next);
      onReach?.(next);
    }, 250);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  const set = (patch) => onChange?.({ kind, regionIds, ...patch });
  const c = (k, unit) => (counts[k] == null ? '' : `${counts[k].toLocaleString()} ${unit}`);
  const tiles = [
    ['everyone', 'EVERYONE', c('everyone', 'users')],
    ['city', 'BY CITY', 'Pick one or more cities'],
    ['hosts', 'HOSTS ONLY', c('hosts', 'active hosts')],
    ['test', 'TEST LIST', c('test', 'staff inboxes')],
  ];
  return (
    <SoftCard title="WHO GETS IT" right={<ReachLine reach={reach} noun="USERS" />} testId="audience-picker">
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }} role="radiogroup" aria-label="Audience">
        {tiles.map(([k, label, note]) => <Tile key={k} on={kind === k} label={label} note={note} disabled={readOnly} onClick={() => set({ kind: k })} testId={`audience-${k}`} />)}
      </div>
      {kind === 'city' && (
        <div className="flex flex-wrap" style={{ gap: 8 }} data-testid="city-picks">
          {cities === null ? <span style={{ fontSize: 11, color: MUTED }}>…</span>
            : cities.length === 0 ? <span style={{ fontSize: 11, color: MUTED }}>No cities with people in them yet.</span>
              : cities.map((city) => {
                const on = regionIds.includes(city.id);
                return (
                  <button key={city.id} type="button" aria-pressed={on} disabled={readOnly}
                    onClick={() => set({ regionIds: on ? regionIds.filter((x) => x !== city.id) : [...regionIds, city.id] })}
                    style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 999, background: on ? '#0A0A0A' : '#F2F2F2', color: on ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>
                    {city.name} · {city.count.toLocaleString()}
                  </button>
                );
              })}
        </div>
      )}
      <OptOutNote reach={reach} />
    </SoftCard>
  );
}
